import { LuckyDraw, Winner } from "../../../Redux/luckydraw/types";
import { LikedStore } from "../../../Redux/store_like/types";
import { MySubmittedReview } from "../../../Redux/storeReview/types";
import { ShoppingVoucherItem } from "../../../Redux/referral/types";

/* ------------------------------------------------------------------ */
/* Shared types (ported from demo MyStuff *Data.ts files)              */
/* ------------------------------------------------------------------ */

export type GiftStatus = "Pending" | "Completed";
export type GiftRewardStatus = "Completed" | "Pending" | "Success";

export type GiftRecord = {
  id: string;
  serialNo: number;
  orderDate: string;
  orderTime: string;
  orderNo: string;
  achievementDate: string;
  milestone: string;
  level: string;
  productName: string;
  orderType: string;
  salarCode: string;
  productImage: string;
  firstMonthReferralIncentive: number;
  secondMonthReferralIncentive: number;
  salarGiftCredits: number;
  validity: string;
  rewardStatus: GiftRewardStatus;
  monthlyReferenceDate: string;
  monthlyStatus: GiftRewardStatus;
  remainingCredits: number;
  remainingTime: string;
  giftStatus: GiftStatus;
  title: string;
};

export type LikedStoreStatus = "Liked" | "Un-Liked";

export type LikedStoreRecord = {
  id: string;
  serialNo: number;
  date: string;
  time: string;
  status: LikedStoreStatus;
  storeName: string;
  address: string;
  contactNumber: string;
  whatsappNumber: string;
  offerCode: string;
  image: string;
  latitude?: number;
  longitude?: number;
  targetStoreId: string;
};

export type ReviewStoreRecord = {
  id: string;
  serialNo: number;
  date: string;
  time: string;
  status: LikedStoreStatus;
  storeName: string;
  address: string;
  contactNumber: string;
  whatsappNumber: string;
  offerCode: string;
  image: string;
  rating: number;
  review: string;
};

export type LuckyDrawStatus = "Delivered" | "Pending" | "Rejected";

export type LuckyDrawRecord = {
  id: string;
  serialNo: number;
  date: string;
  time: string;
  year: number;
  month: string;
  status: LuckyDrawStatus;
  productName: string;
  productDescription: string;
  productImage: string;
  price: number;
  drawReference: string;
  trackingId: string;
  trackingMessage: string;
};

export type OfferStatus = "Active" | "Expired";

export type OfferRecord = {
  id: string;
  serialNo: number;
  date: string;
  purchaseType: string;
  sellerStoreName: string;
  sellerStoreId: string;
  category: string;
  status: OfferStatus;
  productName: string;
  description: string;
  productImage: string;
  price: number;
  quantity: number;
  discountPercent: number;
  expiresIn: string;
};

export type VoucherStatus = "Available" | "Used" | "Expired";

export type VoucherRecord = {
  id: string;
  orderNo: string;
  purchasedOn: string;
  paymentType: string;
  status: VoucherStatus;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  grandTotal: number;
  voucherValue: number;
  validFrom: string;
  validTo: string;
  code: string;
};

export type SubscriptionStatus = "Active" | "Expired";

export type SubscriptionRecord = {
  id: string;
  serialNo: number;
  date: string;
  orderNo: string;
  status: SubscriptionStatus;
  productName: string;
  description: string;
  productImage: string;
  price: number;
  quantity: number;
  validity: string;
  startDate: string;
  endDate: string;
};

/* ------------------------------------------------------------------ */
/* Formatters (ported from demo data files)                            */
/* ------------------------------------------------------------------ */

const toDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value);

export const formatGiftCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const formatVoucherCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);

export const formatStuffDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .format(date)
    .replace(/\//g, "-");
};

export const formatStuffLongDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(/ /g, "-")
    .toUpperCase();
};

export const formatStuffTime = (value?: string) => {
  const date = toDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const formatVoucherDate = (value?: string) => formatStuffLongDate(value);

export const formatVoucherLongDate = (value?: string) => {
  const date = toDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const toClassSlug = (value: string) =>
  value.toLowerCase().trim().replace(/[\s_]+/g, "-");

/* ------------------------------------------------------------------ */
/* Mappers: real Redux data -> demo presentation records               */
/* ------------------------------------------------------------------ */

const IMAGE_FALLBACK = "../../assets/img/placeholder.png";
const GIFT_IMAGE_FALLBACK = "../../assets/img/icons/awards.png";

export const mapWinnerToGiftRecord = (
  winner: Winner,
  index: number,
): GiftRecord => {
  const luckyDraw =
    typeof winner.luckyDrawId === "object" ? winner.luckyDrawId : null;
  const gift =
    typeof winner.giftId === "object" && winner.giftId !== null
      ? winner.giftId
      : null;

  return {
    id: winner._id,
    serialNo: index + 1,
    orderDate: winner.wonAt || "",
    orderTime: formatStuffTime(winner.wonAt),
    orderNo: "N/A",
    achievementDate: winner.wonAt || "",
    milestone: luckyDraw?.name || "Lucky Draw",
    level: "-",
    productName: gift?.name || "Mystery Gift",
    orderType: "-",
    salarCode: "-",
    productImage: gift?.image || gift?.imageUrl || GIFT_IMAGE_FALLBACK,
    firstMonthReferralIncentive: 0,
    secondMonthReferralIncentive: 0,
    salarGiftCredits: 0,
    validity: "-",
    rewardStatus: "Success",
    monthlyReferenceDate: winner.wonAt || "",
    monthlyStatus: "Completed",
    remainingCredits: 0,
    remainingTime: "-",
    giftStatus: "Completed",
    title: "Achievement Reward",
  };
};

export const mapLikedStoreToRecord = (
  store: LikedStore,
  index: number,
): LikedStoreRecord => ({
  id: store._id,
  serialNo: index + 1,
  date: store.createdAt || "",
  time: formatStuffTime(store.createdAt),
  status: "Liked",
  storeName: store.businessName || "Store",
  address: store.location?.address || "N/A",
  contactNumber: store.mobileNo || "N/A",
  whatsappNumber: store.mobileNo || "N/A",
  offerCode: "Offer Code",
  image: store.storeImage || IMAGE_FALLBACK,
  latitude: store.location?.latitude,
  longitude: store.location?.longitude,
  targetStoreId: store.targetStoreId,
});

export const mapReviewToRecord = (
  review: MySubmittedReview,
  index: number,
): ReviewStoreRecord => ({
  id: review._id,
  serialNo: index + 1,
  date: review.createdAt || "",
  time: formatStuffTime(review.createdAt),
  status: "Liked",
  storeName: review.sellerDetails?.businessName || "Store",
  address: "",
  contactNumber: review.sellerDetails?.mobileNo || "N/A",
  whatsappNumber: review.sellerDetails?.mobileNo || "N/A",
  offerCode: "",
  image: review.images?.[0] || IMAGE_FALLBACK,
  rating: review.rating || 0,
  review: review.review || "",
});

const VOUCHER_STATUS_MAP: Record<string, VoucherStatus> = {
  ACTIVE: "Available",
  USED: "Used",
  EXPIRED: "Expired",
};

export const mapVoucherItemToRecord = (
  voucher: ShoppingVoucherItem,
): VoucherRecord => ({
  id: voucher._id || voucher.voucherId || "",
  orderNo: voucher.sourceOrderNo || voucher.ssinId || "-",
  purchasedOn: voucher.createdAt || voucher.validFrom || "",
  paymentType: voucher.source ? voucher.source.replace(/_/g, " ") : "-",
  status: VOUCHER_STATUS_MAP[voucher.status?.toUpperCase()] || "Available",
  title: voucher.sourceProductName || "Salar Shopping Voucher",
  subtitle: `Voucher ${voucher.voucherCode || ""}`,
  description: `Salar Shopping Voucher worth ${formatVoucherCurrency(
    voucher.amount || 0,
  )}. Redeem it before the validity date shown below.`,
  image: voucher.productImage || IMAGE_FALLBACK,
  grandTotal: voucher.amount || 0,
  voucherValue: voucher.remainingAmount ?? voucher.amount ?? 0,
  validFrom: voucher.validFrom || "",
  validTo: voucher.validUntil || "",
  code: voucher.voucherCode || "",
});

const LUCKY_DRAW_STATUS_MAP: Record<string, LuckyDrawStatus> = {
  completed: "Delivered",
  active: "Pending",
  pending: "Pending",
  cancelled: "Rejected",
};

export const mapLuckyDrawToRecord = (
  draw: LuckyDraw,
  index: number,
): LuckyDrawRecord => ({
  id: draw._id,
  serialNo: index + 1,
  date: draw.luckyDrawDateRange?.startDate || draw.createdAt || "",
  time: formatStuffTime(
    draw.luckyDrawDateRange?.startDate || draw.createdAt,
  ),
  year: new Date(
    draw.luckyDrawDateRange?.endDate || draw.createdAt || Date.now(),
  ).getFullYear(),
  month: new Intl.DateTimeFormat("en-GB", { month: "long" }).format(
    new Date(
      draw.luckyDrawDateRange?.endDate || draw.createdAt || Date.now(),
    ),
  ),
  status: LUCKY_DRAW_STATUS_MAP[draw.status] || "Pending",
  productName: draw.name || "Lucky Draw",
  productDescription: `Participate and stand a chance to win! ${draw.analytics?.totalParticipants ?? 0} participants so far.`,
  productImage: draw.media?.images?.[0] || IMAGE_FALLBACK,
  price: 0,
  drawReference: draw._id,
  trackingId: "-",
  trackingMessage: `${draw.analytics?.totalWinners ?? 0} winners of ${draw.maxWinners ?? 0} max`,
});
